import React, { useState, useEffect } from 'react';
import { getFirestore, collection, getDocs, deleteDoc, doc, updateDoc, query, orderBy } from 'firebase/firestore';
import { getStorage, ref, deleteObject } from 'firebase/storage';
import { useNavigate } from 'react-router-dom';
import { auth } from '../../config/firebase';
import { signOut } from 'firebase/auth';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { SortableNewsCard } from './SortableNewsCard';
import AddButton from '../../components/AddButton/AddButton';
import CreateNewsForm from '../../components/CreateNewsForm/CreateNewsForm';
import EditNewsForm from '../../components/EditNewsForm/EditNewsForm';
import LazyImage from '../../components/ui/LazyImage/LazyImage';
import './News.css';

const News = () => {
  const navigate = useNavigate();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [newsToEdit, setNewsToEdit] = useState(null);
  
  // Initialiser les états avec les valeurs du localStorage
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('newsViewMode') || 'grid';
  });
  
  const [selectedCategory, setSelectedCategory] = useState(() => {
    return localStorage.getItem('newsSelectedCategory') || 'all';
  });
  
  const [sortOrder, setSortOrder] = useState(() => {
    return localStorage.getItem('newsSortOrder') || 'desc';
  });

  // Sauvegarder les changements dans le localStorage
  useEffect(() => {
    localStorage.setItem('newsViewMode', viewMode);
  }, [viewMode]);

  useEffect(() => {
    localStorage.setItem('newsSelectedCategory', selectedCategory);
  }, [selectedCategory]);

  useEffect(() => {
    localStorage.setItem('newsSortOrder', sortOrder);
  }, [sortOrder]);

  // Gestionnaires d'événements avec mise à jour du localStorage
  const handleViewModeChange = (mode) => {
    setViewMode(mode);
  };

  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
  };

  const handleSortOrderChange = (e) => {
    setSortOrder(e.target.value);
  };

  const [categories, setCategories] = useState([]);

  const fetchNews = async () => {
    try {
      setLoading(true);
      const db = getFirestore();
      const newsRef = collection(db, 'nouveautes');
      const q = query(newsRef, orderBy('order', 'asc'));
      const snapshot = await getDocs(q);
      const newsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Extraire les catégories uniques
      const uniqueCategories = [...new Set(newsList.map(item => item.categorie))];
      setCategories(uniqueCategories);
      
      setNews(newsList);
    } catch (error) {
      console.error("Erreur lors de la récupération des nouveautés:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNews();
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    }
  };

  const handleCreateSuccess = async () => {
    setShowCreateForm(false);
    await fetchNews(); // Rafraîchit la liste après création
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;

    setIsDeleting(true);
    try {
      const db = getFirestore();
      const storage = getStorage();

      // Supprimer l'image de Storage
      if (itemToDelete.imageUrl) {
        const imageRef = ref(storage, itemToDelete.imageUrl);
        await deleteObject(imageRef).catch(error => {
          console.log("L'image n'existe peut-être plus dans le storage:", error);
        });
      }

      // Supprimer le document de Firestore
      await deleteDoc(doc(db, 'nouveautes', itemToDelete.id));

      // Rafraîchir la liste
      await fetchNews();
      
      setShowDeleteConfirm(false);
      setItemToDelete(null);
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert('Une erreur est survenue lors de la suppression');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setItemToDelete(null);
  };

  const handleEditClick = (item) => {
    setNewsToEdit(item);
    setShowEditForm(true);
  };

  const handleEditSuccess = async () => {
    setShowEditForm(false);
    setNewsToEdit(null);
    await fetchNews();
  };

  const filteredNews = selectedCategory === 'all' 
    ? news 
    : news.filter(item => item.categorie === selectedCategory);

  // Trier les nouveautés par leur champ 'order'
  const sortedAndFilteredNews = [...filteredNews].sort((a, b) => {
    const orderA = typeof a.order === 'number' ? a.order : Infinity;
    const orderB = typeof b.order === 'number' ? b.order : Infinity;
    return orderA - orderB;
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (active.id !== over.id) {
      setNews((currentNewsState) => {
        const oldIndex = currentNewsState.findIndex((item) => item.id === active.id);
        const newIndex = currentNewsState.findIndex((item) => item.id === over.id);

        if (oldIndex === -1 || newIndex === -1) {
          console.warn('Item ID not found during drag operation, aborting state update.');
          return currentNewsState; // IDs non trouvés, ne rien faire
        }

        // 1. Réorganiser les items
        let movedItems = arrayMove(currentNewsState, oldIndex, newIndex);

        // 2. Mettre à jour la propriété 'order' de chaque item dans la liste réorganisée
        // pour qu'elle corresponde à leur nouvelle position (index).
        movedItems = movedItems.map((item, index) => ({
          ...item,
          order: index 
        }));

        // 3. Mettre à jour Firestore en arrière-plan
        // On utilise une transaction ou un batch pour assurer la cohérence si possible,
        // mais pour l'instant, des mises à jour individuelles.
        const db = getFirestore();
        movedItems.forEach(async (item) => {
          const itemRef = doc(db, 'nouveautes', item.id);
          await updateDoc(itemRef, { order: item.order }); 
        });

        return movedItems; // Retourner les items avec l'ordre physique ET la propriété 'order' mis à jour
      });
    }
  };

  // Fonction pour initialiser l'ordre des documents existants
  const initializeOrder = async () => {
    try {
      const db = getFirestore();
      const newsRef = collection(db, 'nouveautes');
      const snapshot = await getDocs(newsRef);
      
      // Trier les documents par date de création
      const sortedDocs = snapshot.docs.sort((a, b) => {
        const dateA = new Date(a.data().dateCreation);
        const dateB = new Date(b.data().dateCreation);
        return dateB - dateA;
      });

      // Mettre à jour chaque document avec son ordre
      const batch = db.batch();
      sortedDocs.forEach((doc, index) => {
        const docRef = doc.ref;
        batch.update(docRef, { order: index });
      });

      await batch.commit();
      await fetchNews(); // Rafraîchir la liste
    } catch (error) {
      console.error('Erreur lors de l\'initialisation de l\'ordre:', error);
    }
  };

  // Appeler initializeOrder une seule fois au chargement
  useEffect(() => {
    const initializeOrderOnce = async () => {
      const hasInitialized = localStorage.getItem('newsOrderInitialized');
      if (!hasInitialized) {
        await initializeOrder();
        localStorage.setItem('newsOrderInitialized', 'true');
      }
    };
    initializeOrderOnce();
  }, []);

  return (
    <div className="news">
      <div className="news-content">
        <div className="dashboard-container">
          <main className="main-content">
            <div className="news-header">
              <h1>Nouveautés</h1>
              <div className="header-actions">
                <div className="category-filter">
                  <select 
                    value={selectedCategory}
                    onChange={handleCategoryChange}
                    className="category-select"
                  >
                    <option value="all">Toutes les catégories</option>
                    {categories.map(category => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="sort-filter">
                  <select
                    value={sortOrder}
                    onChange={handleSortOrderChange}
                    className="sort-select"
                  >
                    <option value="desc">Plus récent</option>
                    <option value="asc">Plus ancien</option>
                  </select>
                </div>
                <div className="view-toggle">
                  <button 
                    className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                    onClick={() => handleViewModeChange('grid')}
                  >
                    <i className="fas fa-th-large"></i>
                  </button>
                  <button 
                    className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
                    onClick={() => handleViewModeChange('list')}
                  >
                    <i className="fas fa-list"></i>
                  </button>
                </div>
                <div className="button-container">
                  <AddButton onClick={() => setShowCreateForm(true)} />
                </div>
              </div>
            </div>

            {showCreateForm && (
              <div className="modal-overlay">
                <CreateNewsForm 
                  onClose={() => setShowCreateForm(false)}
                  onSuccess={handleCreateSuccess}
                />
              </div>
            )}

            <div className={`news-container ${viewMode}`}>
              {loading ? (
                <div className="loading">Chargement...</div>
              ) : sortedAndFilteredNews.length === 0 ? (
                <div className="no-results">Aucune nouveauté dans cette catégorie</div>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={sortedAndFilteredNews.map(item => item.id)}
                    strategy={viewMode === 'grid' ? rectSortingStrategy : verticalListSortingStrategy}
                  >
                    {sortedAndFilteredNews.map(item => (
                      <SortableNewsCard
                        key={item.id}
                        item={item}
                        viewMode={viewMode}
                        onEdit={handleEditClick}
                        onDelete={handleDeleteClick}
                        isDeleting={isDeleting}
                        itemToDelete={itemToDelete}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              )}
            </div>

            {showDeleteConfirm && (
              <div className="modal-overlay">
                <div className="confirm-modal">
                  <h3>Confirmer la suppression</h3>
                  <p>Êtes-vous sûr de vouloir supprimer "{itemToDelete?.titre}" ?</p>
                  <p className="warning-text">Cette action est irréversible.</p>
                  <div className="confirm-actions">
                    <button 
                      className="cancel-btn"
                      onClick={handleDeleteCancel}
                      disabled={isDeleting}
                    >
                      Annuler
                    </button>
                    <button 
                      className="delete-btn"
                      onClick={handleDeleteConfirm}
                      disabled={isDeleting}
                    >
                      {isDeleting ? 'Suppression...' : 'Supprimer'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {showEditForm && (
              <EditNewsForm 
                news={newsToEdit}
                onClose={() => {
                  setShowEditForm(false);
                  setNewsToEdit(null);
                }}
                onSuccess={handleEditSuccess}
              />
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default News; 